import { createContext, useContext, useState } from "react";

const translations = {
  en: {
    nav: {
      about: "The Festival",
      program: "Program",
      exhibitors: "Maestri",
      visit: "Visit",
      tickets: "Tickets",
      order: "Order Online",
      sponsors: "Sponsors",
      contact: "Contact",
      cta: "Attend",
    },
    hero: {
      overline: "The Italian Christmas tradition, in the heart of New York",
      line1: "NEW YORK",
      line2: "Panettone",
      line3: "FESTIVAL",
      dates: "December 11–13, 2026",
      venue: "Istituto Italiano di Cultura",
      address: "686 Park Ave, New York, NY 10065",
      ctaVisitor: "Attend the Festival",
      ctaExhibitor: "Become an Exhibitor",
      edition: "6th Edition",
      scroll: "Scroll",
    },
    countdown: {
      prefix: "The festival begins in",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds",
    },
    marquee: [
      "Celebrating Tradition, Embracing Innovation",
      "December 11–13, 2026",
      "Istituto Italiano di Cultura · 686 Park Ave",
      "Maestri Pasticcieri Italiani",
      "New York Panettone Festival",
    ],
    about: {
      overline: "Our Story",
      title: "A love letter to panettone, written in New York",
      p1: "Born from a simple, audacious idea — bringing Italy's most celebrated Maestri Pasticcieri to New York — the New York Panettone Festival is the first event of its kind ever held in the United States.",
      p2: "The festival redefines the culinary and cultural experience of panettone, offering the chance to taste and learn about the countless excellences that have made this leavened masterpiece one of the highest expressions of Italian pastry.",
      p3: "Year after year the festival has become a truly international affair, bringing together the culinary traditions of Italy and the United States — a fusion of talents and tastes celebrating the global love for panettone and the exchange of ideas and techniques across borders.",
      quote: "“I chose panettone because I was fascinated by its complexity — indeed, we could also say that it was the panettone that chose me.”",
      quoteBy: "Alessandro Slama, Maestro Pasticciere",
      stats: [
        { value: "18,000", label: "panettoni sold in a single weekend (2024)" },
        { value: "8,000", label: "visitors at the 2024 edition" },
        { value: "21", label: "Italian & American Maestri Pasticcieri (2025)" },
        { value: "6th", label: "edition — December 2026" },
      ],
      timelineTitle: "Past editions",
      timeline: [
        { year: "2021", text: "The first edition debuts in NYC with a kickoff appearance by Lidia Bastianich. Two weekends, panettoni sold out online." },
        { year: "2022", text: "4,500 attendees and 12,000 panettoni sold in a single weekend." },
        { year: "2023", text: "7,500 attendees, 15,000 panettoni sold — online stock gone by late November." },
        { year: "2024", text: "8,000 attendees and 18,000 panettoni sold over the weekend." },
        { year: "2025", text: "The festival lights up The Piazza at Hunter's Point South Park with 16 Italian and 5 American pastry chefs." },
        { year: "2026", text: "A new home: the Italian Cultural Institute, 686 Park Ave — December 11–13." },
      ],
    },
    program: {
      overline: "The Program",
      title: "Three days of taste, music and wonder",
      note: "The 2026 program: one day to learn, two to celebrate together.",
      days: [
        { date: "11", dow: "Friday", tag: "Masterclass", title: "Panettone Class", time: "6:30 PM", text: "A day of hands-on classes with the Maestri: lievito madre, kneading and the secret of the perfect dome." },
        { date: "12", dow: "Saturday", tag: "Festival", title: "The Festival — Day 1", time: "10 AM – 7 PM", text: "All the Maestri Pasticcieri exhibit their creations: tastings, shopping and the magic of panettone." },
        { date: "13", dow: "Sunday", tag: "Festival", title: "The Festival — Day 2", time: "10 AM – 7 PM", text: "Second and final day: the full exhibition of every Maestro's panettone, before the sell-out." },
      ],
    },
    exhibitors: {
      overline: "The Maestri",
      title: "Italy's greatest pastry masters, in New York",
      note: "The lineup from our latest editions. The 2026 lineup will be announced soon.",
      cta: "Exhibit with us",
    },
    order: {
      overline: "Online Shop",
      title: "Order your panettone online",
      text: "Every year our panettoni sell out online before the festival even begins. Order yours and bring the excellence of the Maestri to your table.",
      cta: "Order Online",
      comingSoon: "The online shop link will be published soon. Leave us your email in the visitor form and we will let you know.",
      note: "Online orders via our official Square store.",
    },
    tickets: {
      overline: "Tickets",
      title: "Free admission — just reserve your spot",
      text: "Entry to the New York Panettone Festival is completely free. All you need to do is book: reserve your free spot in a few seconds and choose your preferred day.",
      cta: "Reserve for Free",
      note: "Free admission · Free reservation required",
    },
    participate: {
      overline: "Join Us",
      title: "Be part of the festival",
      tabVisitor: "Attend as a Visitor",
      tabClass: "Panettone Class",
      tabExhibitor: "Exhibit with Us",
      classForm: {
        title: "Book the Panettone Class",
        text: "A hands-on masterclass with the Maestri at the Italian Cultural Institute. Free admission, limited spots.",
        info: "Friday, December 11, 2026 · 6:30 PM · 686 Park Ave",
        seats: "spots still available",
        classFull: "The Panettone Class is fully booked.",
        soldOutTitle: "Class fully booked",
        soldOutText: "Unfortunately all 70 spots for the Panettone Class are taken. You can still sign up as a visitor for Saturday or Sunday.",
        successTitle: "You're booked for the class!",
        success: "Your spot for the Panettone Class (Friday, December 11, 6:30 PM) is reserved. Check your inbox for the confirmation.",
      },
      visitor: {
        title: "Reserve your visit",
        name: "Full name",
        email: "Email",
        guests: "Number of guests",
        day: "Preferred day",
        days: ["Saturday, December 12", "Sunday, December 13"],
        submit: "Sign Up",
        sending: "Sending…",
        successTitle: "You're on the list!",
        success: "Check your inbox: a confirmation is on its way. See you December 11–13 at 686 Park Ave.",
      },
      exhibitor: {
        title: "Apply as an exhibitor",
        business: "Business name",
        contact: "Contact person",
        email: "Email",
        phone: "Phone",
        website: "Website or Instagram",
        description: "Tell us about your products",
        submit: "Submit Application",
        sending: "Sending…",
        successTitle: "Application received!",
        success: "Thank you! A confirmation email is on its way and we will be in touch soon.",
      },
      error: "Something went wrong. Please try again.",
      tooMany: "Too many attempts in a short time. Please wait a minute and try again.",
      successToast: "Sent successfully! Check your inbox for the confirmation.",
    },
    visit: {
      overline: "Getting There",
      title: "In the heart of the Upper East Side",
      venue: "Istituto Italiano di Cultura di New York",
      address: "686 Park Ave, New York, NY 10065",
      dates: "December 11–13, 2026",
      hours: "Sat & Sun 10 AM – 7 PM · Fri: Panettone Class 6:30 PM",
      directions: "Get Directions",
      transportTitle: "How to reach us",
      transport: "Detailed directions by subway, bus and car will be published soon.",
    },
    sponsors: {
      overline: "Sponsors & Partners",
      title: "With the support of",
      press: "As seen in",
    },
    contact: {
      overline: "Contact",
      title: "Talk to us",
      email: "Email",
      phone: "Phone",
      instagram: "Instagram",
    },
    footer: {
      tagline: "Celebrating Tradition, Embracing Innovation.",
      edition: "December 11–13, 2026 · New York",
      rights: "All rights reserved.",
    },
  },
  it: {
    nav: {
      about: "Il Festival",
      program: "Programma",
      exhibitors: "Maestri",
      visit: "Come Arrivare",
      tickets: "Biglietti",
      order: "Ordina Online",
      sponsors: "Sponsor",
      contact: "Contatti",
      cta: "Partecipa",
    },
    hero: {
      overline: "La tradizione natalizia italiana, nel cuore di New York",
      line1: "NEW YORK",
      line2: "Panettone",
      line3: "FESTIVAL",
      dates: "11–13 dicembre 2026",
      venue: "Istituto Italiano di Cultura",
      address: "686 Park Ave, New York, NY 10065",
      ctaVisitor: "Partecipa come Visitatore",
      ctaExhibitor: "Diventa Espositore",
      edition: "6ª Edizione",
      scroll: "Scorri",
    },
    countdown: {
      prefix: "Il festival inizia tra",
      days: "Giorni",
      hours: "Ore",
      minutes: "Minuti",
      seconds: "Secondi",
    },
    marquee: [
      "Celebrating Tradition, Embracing Innovation",
      "11–13 dicembre 2026",
      "Istituto Italiano di Cultura · 686 Park Ave",
      "Maestri Pasticcieri Italiani",
      "New York Panettone Festival",
    ],
    about: {
      overline: "La Nostra Storia",
      title: "Una lettera d'amore al panettone, scritta a New York",
      p1: "Nato da un'idea semplice e audace — portare i più celebri Maestri Pasticcieri d'Italia a New York — il New York Panettone Festival è il primo evento di questo genere mai realizzato negli Stati Uniti.",
      p2: "Il festival ridefinisce l'esperienza culinaria e culturale del panettone, offrendo l'opportunità di assaggiare e conoscere le innumerevoli eccellenze che hanno reso questo capolavoro lievitato una delle massime espressioni della pasticceria italiana.",
      p3: "Anno dopo anno il festival è diventato un appuntamento davvero internazionale, unendo le tradizioni culinarie d'Italia e degli Stati Uniti: una fusione di talenti e sapori che celebra l'amore globale per il panettone e lo scambio di idee e tecniche oltre i confini.",
      quote: "«Ho scelto il panettone perché ero affascinato dalla sua complessità — anzi, potremmo anche dire che è stato il panettone a scegliere me.»",
      quoteBy: "Alessandro Slama, Maestro Pasticciere",
      stats: [
        { value: "18.000", label: "panettoni venduti in un solo weekend (2024)" },
        { value: "8.000", label: "visitatori all'edizione 2024" },
        { value: "21", label: "Maestri Pasticcieri italiani e americani (2025)" },
        { value: "6ª", label: "edizione — dicembre 2026" },
      ],
      timelineTitle: "Le edizioni passate",
      timeline: [
        { year: "2021", text: "La prima edizione debutta a NYC con l'apertura affidata a Lidia Bastianich. Due weekend, panettoni sold out online." },
        { year: "2022", text: "4.500 presenze e 12.000 panettoni venduti in un solo weekend." },
        { year: "2023", text: "7.500 presenze, 15.000 panettoni venduti — stock online esaurito già a fine novembre." },
        { year: "2024", text: "8.000 presenze e 18.000 panettoni venduti nel weekend." },
        { year: "2025", text: "Il festival illumina The Piazza at Hunter's Point South Park con 16 pasticceri italiani e 5 americani." },
        { year: "2026", text: "Una nuova casa: l'Istituto Italiano di Cultura, 686 Park Ave — 11–13 dicembre." },
      ],
    },
    program: {
      overline: "Il Programma",
      title: "Tre giorni di gusto, musica e meraviglia",
      note: "Il programma 2026: un giorno per imparare, due per festeggiare insieme.",
      days: [
        { date: "11", dow: "Venerdì", tag: "Masterclass", title: "Panettone Class", time: "18:30", text: "Una giornata di lezioni pratiche con i Maestri: lievito madre, impasti e il segreto della cupola perfetta." },
        { date: "12", dow: "Sabato", tag: "Festival", title: "Il Festival — Giorno 1", time: "10:00 – 19:00", text: "Tutti i Maestri Pasticcieri espongono le loro creazioni: degustazioni, shopping e la magia del panettone." },
        { date: "13", dow: "Domenica", tag: "Festival", title: "Il Festival — Giorno 2", time: "10:00 – 19:00", text: "Secondo e ultimo giorno: l'esposizione completa dei panettoni di ogni Maestro, prima del sold out." },
      ],
    },
    exhibitors: {
      overline: "I Maestri",
      title: "I più grandi maestri pasticceri d'Italia, a New York",
      note: "La line-up della nostra ultima edizione. La line-up 2026 sarà annunciata presto.",
      cta: "Esponi con noi",
    },
    order: {
      overline: "Shop Online",
      title: "Ordina il tuo panettone online",
      text: "Ogni anno i nostri panettoni vanno sold out online prima ancora dell'inizio del festival. Ordina il tuo e porta in tavola l'eccellenza dei Maestri.",
      cta: "Ordina Online",
      comingSoon: "Il link dello shop online sarà pubblicato presto. Lasciaci la tua email nel modulo visitatori e ti avviseremo noi.",
      note: "Ordini online tramite il nostro store ufficiale Square.",
    },
    tickets: {
      overline: "Biglietti",
      title: "Ingresso gratuito — basta prenotare",
      text: "L'ingresso al New York Panettone Festival è completamente gratuito. Devi solo prenotare: riserva il tuo posto gratuito in pochi secondi e scegli il giorno che preferisci.",
      cta: "Prenota Gratis",
      note: "Ingresso gratuito · Prenotazione gratuita obbligatoria",
    },
    participate: {
      overline: "Partecipa",
      title: "Entra a far parte del festival",
      tabVisitor: "Partecipa come Visitatore",
      tabClass: "Panettone Class",
      tabExhibitor: "Partecipa come Espositore",
      classForm: {
        title: "Prenota la Panettone Class",
        text: "Una masterclass pratica con i Maestri all'Istituto Italiano di Cultura. Ingresso gratuito, posti limitati.",
        info: "Venerdì 11 dicembre 2026 · ore 18:30 · 686 Park Ave",
        seats: "posti ancora disponibili",
        classFull: "La Panettone Class è al completo.",
        soldOutTitle: "Masterclass al completo",
        soldOutText: "Purtroppo i 70 posti della Panettone Class sono esauriti. Puoi comunque iscriverti come visitatore per sabato o domenica.",
        successTitle: "Posto prenotato per la masterclass!",
        success: "Il tuo posto per la Panettone Class (venerdì 11 dicembre, ore 18:30) è riservato. Controlla la tua email per la conferma.",
      },
      visitor: {
        title: "Riserva la tua visita",
        name: "Nome e cognome",
        email: "Email",
        guests: "Numero di partecipanti",
        day: "Giorno di interesse",
        days: ["Sabato 12 dicembre", "Domenica 13 dicembre"],
        submit: "Iscriviti",
        sending: "Invio in corso…",
        successTitle: "Sei in lista!",
        success: "Controlla la tua casella: la conferma è in arrivo. Ti aspettiamo dall'11 al 13 dicembre al 686 Park Ave.",
      },
      exhibitor: {
        title: "Candidati come espositore",
        business: "Nome dell'attività",
        contact: "Referente",
        email: "Email",
        phone: "Telefono",
        website: "Sito o Instagram",
        description: "Raccontaci i tuoi prodotti",
        submit: "Invia la Candidatura",
        sending: "Invio in corso…",
        successTitle: "Candidatura ricevuta!",
        success: "Grazie! Ti abbiamo inviato una conferma via email e ti ricontatteremo presto.",
      },
      error: "Qualcosa è andato storto. Riprova.",
      tooMany: "Troppi tentativi in poco tempo. Attendi un minuto e riprova.",
      successToast: "Inviato con successo! Controlla la tua email per la conferma.",
    },
    visit: {
      overline: "Come Arrivare",
      title: "Nel cuore dell'Upper East Side",
      venue: "Istituto Italiano di Cultura di New York",
      address: "686 Park Ave, New York, NY 10065",
      dates: "11–13 dicembre 2026",
      hours: "Sab e Dom 10:00 – 19:00 · Ven: Panettone Class 18:30",
      directions: "Indicazioni Stradali",
      transportTitle: "Come raggiungerci",
      transport: "Le indicazioni dettagliate in metropolitana, bus e auto saranno pubblicate presto.",
    },
    sponsors: {
      overline: "Sponsor & Partner",
      title: "Con il sostegno di",
      press: "Hanno parlato di noi",
    },
    contact: {
      overline: "Contatti",
      title: "Parliamone",
      email: "Email",
      phone: "Telefono",
      instagram: "Instagram",
    },
    footer: {
      tagline: "Celebrating Tradition, Embracing Innovation.",
      edition: "11–13 dicembre 2026 · New York",
      rights: "Tutti i diritti riservati.",
    },
  },
};

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  return (
    <LangContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
